/**
 * Affichage des montants. La devise est OBLIGATOIRE dans le type des props :
 * `<MoneyText amount="150.00" />` ne compile pas. Aucune devise par défaut
 * n'existe dans ce module (BUG-6 : tout le financier était libellé EUR sur des
 * MAD).
 */
import React from 'react';
import { Tag, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import type { Cost, MoneyMulti } from '../../../api/monitoring/v2';
import CompactValue from './CompactValue';

export type MoneyTextProps = {
    /** Chaîne décimale exacte du serveur (format compact, exacte en infobulle). */
    amount: string;
    /** Code ISO 4217. Obligatoire. */
    currency: string;
};

export const MoneyText: React.FC<MoneyTextProps> = ({ amount, currency }) => <CompactValue value={amount} currency={currency} />;

/**
 * Montants multi-devises : une ligne par devise, JAMAIS additionnées entre
 * elles (contrat §1, MON-11-F).
 */
export const MoneyMultiText: React.FC<{ values: MoneyMulti; inline?: boolean }> = ({ values, inline }) => {
    if (!values.length) return <span>—</span>;
    return (
        <span style={{ display: 'inline-flex', flexDirection: inline ? 'row' : 'column', gap: inline ? 8 : 0 }}>
            {values.map((m) => (
                <span key={m.currency} data-currency={m.currency}>
                    <MoneyText amount={m.amount} currency={m.currency} />
                </span>
            ))}
        </span>
    );
};

/**
 * Coût IA (`cost`), au format compact (2 décimales sous 1 000) ; les coûts se
 * comptent souvent en fractions de centime : toutes les décimales reçues
 * restent lisibles dans l'infobulle.
 * `partial` : des appels non valorisables existent ; `none` : aucun appel
 * valorisable — affiché comme tel, jamais comme « 0 ».
 */
export const CostText: React.FC<{ cost: Cost }> = ({ cost }) => {
    const { t } = useTranslation();
    if (cost.status === 'none') {
        return <span>{t('monitoring.cost.status.none')}</span>;
    }
    const text = <CompactValue value={cost.amount} currency={cost.currency} />;
    if (cost.status === 'partial') {
        return (
            <span>
                {text}{' '}
                <Tooltip title={t('monitoring.cost.partialHelp')}>
                    <Tag bordered={false} tabIndex={0} style={{ marginInlineStart: 4 }}>
                        {t('monitoring.cost.status.partial')}
                    </Tag>
                </Tooltip>
            </span>
        );
    }
    return text;
};

export default MoneyText;
